import { useState } from 'react';

import { Button, Card, Table } from 'antd';
import { useTranslation } from 'react-i18next';

import { COMPLIANCE_COLORS } from 'scenes/VisitHistory/utils/charts';
import { complianceLevel } from 'scenes/VisitHistory/utils/compliance';
import { formatDecimal, formatInt } from 'scenes/VisitHistory/utils/format';
import { dayHeaderLabel } from 'scenes/VisitHistory/utils/mapData';

import { DaySummary } from '../utils/storeVisits';

const I18N_BASE = 'scenes.store_visits.days';
const DEFAULT_ROWS = 10;

interface StoreVisitsDaySummaryProps {
  days: DaySummary[];
  highlightDate: string | null;
  onOpenDay: (date: string) => void;
}

export function ComplianceBar({ pct }: { pct: number }) {
  const { i18n } = useTranslation();
  return (
    <span className="store-visits-compliance">
      <span className="store-visits-compliance__track">
        <span
          style={{
            width: `${Math.min(100, pct)}%`,
            background: COMPLIANCE_COLORS[complianceLevel(pct)],
          }}
        />
      </span>
      <span
        className={`store-visits-compliance__pct store-visits-compliance__pct--${complianceLevel(
          pct
        )}`}
      >
        {`${formatDecimal(pct, i18n.language)}%`}
      </span>
    </span>
  );
}

/** Una fila por día con rutas; el modal trae las rutas solo al abrirlo. */
function StoreVisitsDaySummary({
  days,
  highlightDate,
  onOpenDay,
}: StoreVisitsDaySummaryProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? days : days.slice(0, DEFAULT_ROWS);
  const int = (n: number) => formatInt(n, language);

  return (
    <Card
      className="store-visits-days"
      title={
        <>
          {t(`${I18N_BASE}.title`)}
          <span className="visit-history-inc__sub">
            {t(`${I18N_BASE}.subtitle`)}
          </span>
        </>
      }
    >
      <Table<DaySummary>
        rowKey="date"
        size="middle"
        dataSource={rows}
        pagination={false}
        scroll={{ x: 820 }}
        rowClassName={(row) =>
          row.date === highlightDate ? 'store-visits-days__row--active' : ''
        }
        columns={[
          {
            title: t(`${I18N_BASE}.columns.day`),
            key: 'date',
            render: (_, row) => (
              <strong>{dayHeaderLabel(row.date, language)}</strong>
            ),
          },
          {
            title: t(`${I18N_BASE}.columns.routes`),
            key: 'routes',
            render: (_, row) => (
              <>
                {int(row.routes)}
                {row.routesWithIncidents > 0 && (
                  <span className="store-visits-days__muted">
                    {` · ${t(`${I18N_BASE}.with_incidents`, {
                      count: row.routesWithIncidents,
                    })}`}
                  </span>
                )}
              </>
            ),
          },
          {
            title: t(`${I18N_BASE}.columns.stores`),
            key: 'planned',
            align: 'right',
            render: (_, row) => int(row.planned),
          },
          {
            title: t(`${I18N_BASE}.columns.executed`),
            key: 'executed',
            align: 'right',
            render: (_, row) => int(row.executed),
          },
          {
            title: t(`${I18N_BASE}.columns.incidents`),
            key: 'incidents',
            align: 'right',
            render: (_, row) => int(row.incidents),
          },
          {
            title: t(`${I18N_BASE}.columns.compliance`),
            key: 'compliance',
            width: 170,
            render: (_, row) => <ComplianceBar pct={row.compliancePct} />,
          },
          {
            title: t(`${I18N_BASE}.columns.material`),
            key: 'material',
            align: 'right',
            render: (_, row) => int(row.materialUnits),
          },
          {
            key: 'actions',
            align: 'right',
            render: (_, row) => (
              <Button size="small" onClick={() => onOpenDay(row.date)}>
                {t(`${I18N_BASE}.open`)}
              </Button>
            ),
          },
        ]}
      />
      {days.length > DEFAULT_ROWS && (
        <Button
          type="link"
          className="store-visits-days__more"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? t(`${I18N_BASE}.show_less`)
            : t(`${I18N_BASE}.show_all`, { count: days.length })}
        </Button>
      )}
    </Card>
  );
}

export default StoreVisitsDaySummary;
